import { useState } from "react";
import { motion } from "framer-motion";
import { ShieldCheck } from "lucide-react";
import { renderAA } from "../aa";

// Concept 1 — The Architectural Layer Stack
// 4 capability tiers build up on the left, FDE accelerator spine on the right,
// entire structure wrapped in an "AI TRiSM" governance frame.
export const ConceptStack = ({ core, fde, trism }) => {
  const [fdeHover, setFdeHover] = useState(false);
  const tiers = [...core].reverse(); // Business Applications (top) -> Semantic Core (base)

  return (
    <div className="h-full w-full bg-[#F7F5F0] p-6 lg:p-8 flex flex-col" data-testid="concept-stack">
      <div className="mb-4">
        <span className="font-mono2 text-[10px] uppercase tracking-[0.3em] text-[#8A5730]">The Platform</span>
        <h2 className="font-display text-2xl lg:text-3xl font-medium text-[#3E2818] tracking-tight mt-1 leading-tight">
          What It Takes to Achieve {renderAA("Agentic Alpha")}
        </h2>
      </div>

      {/* TRiSM governance frame */}
      <div className="relative flex-1 min-h-0 rounded-2xl border border-[#E8AA68]/70 bg-[rgba(232,170,104,0.05)] p-4 lg:p-5 flex flex-col">
        <div className="absolute -top-2.5 left-6 flex items-center gap-2 bg-[#F7F5F0] px-3">
          <ShieldCheck size={13} className="text-[#8A5730]" />
          <span className="font-mono2 text-[10px] uppercase tracking-[0.25em] text-[#8A5730]">
            {trism.title} · Governance &amp; Trust
          </span>
        </div>

        <div className="grid grid-cols-12 gap-4 flex-1 min-h-0 mt-2">
          {/* Tiered stack */}
          <div className="col-span-12 lg:col-span-8 flex flex-col gap-2.5 min-h-0">
            {tiers.map((t, i) => (
              <motion.div
                key={t.title}
                animate={{ boxShadow: fdeHover ? "0 0 0 2px #E8AA68" : "0 0 0 0 rgba(232,170,104,0)" }}
                transition={{ duration: 0.35 }}
                className="flex-1 min-h-0 rounded-xl bg-white border border-[#3E2818]/10 px-4 flex items-center gap-4"
                data-testid={`stack-tier-${i}`}
              >
                <div className="shrink-0 w-40">
                  <span className="font-mono2 text-[10px] text-[#8A5730]">Layer {tiers.length - i}</span>
                  <h3 className="font-display text-base lg:text-lg font-medium text-[#3E2818] leading-tight">{t.title}</h3>
                </div>
                <div className="hidden md:flex flex-wrap gap-1.5 justify-end flex-1">
                  {t.features.map((f) => (
                    <span key={f.title} className="text-[11px] px-2.5 py-1 rounded-full bg-[#F7F5F0] text-[#3E2818]/70 border border-[#3E2818]/10">
                      {f.title}
                    </span>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>

          {/* FDE accelerator spine */}
          <motion.div
            onHoverStart={() => setFdeHover(true)}
            onHoverEnd={() => setFdeHover(false)}
            className="col-span-12 lg:col-span-4 rounded-xl bg-[#3E2818] text-[#F7F5F0] p-4 lg:p-5 flex flex-col cursor-default"
            data-testid="stack-fde"
          >
            <span className="font-mono2 text-[10px] uppercase tracking-[0.25em] text-[#E8AA68]">AI Accelerator</span>
            <h3 className="font-display text-lg lg:text-xl font-medium mt-1 leading-tight">{fde.title}</h3>
            <p className="text-[12px] text-white/70 mt-2 leading-snug">{fde.promise}</p>
            <div className="mt-auto space-y-2.5 pt-4">
              {fde.features.map((f) => (
                <div key={f.title} className="flex items-center gap-2.5 text-[12px] text-white/85">
                  <f.icon size={14} className="text-[#E8AA68] shrink-0" strokeWidth={1.9} />
                  {f.title}
                </div>
              ))}
            </div>
          </motion.div>
        </div>

        {/* TRiSM trust strip */}
        <div className="flex flex-wrap items-center gap-x-6 gap-y-1.5 mt-3 pt-3 border-t border-[#E8AA68]/30">
          {trism.features.map((f) => (
            <div key={f.title} className="flex items-center gap-1.5 text-[11px] text-[#8A5730]">
              <f.icon size={13} strokeWidth={1.9} /> {f.title}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
